import { HttpErrorResponse } from '@angular/common/http';
import * as Sentry from '@sentry/browser';

import { environment } from 'environments/environment';

import { getDevicePlatform } from 'helpers';

/** Response bodies can be whole HTML error pages, so only keep enough to diagnose. */
const MAX_BODY_LENGTH = 2000;

/** Keep the Sentry title low cardinality so events group together. */
const MAX_SUMMARY_LENGTH = 200;

export interface HandledErrorContext {
  /**
   * Stable label for what failed, e.g. `applyTag.fileUpload`. Becomes part of
   * the Sentry issue title, so each operation groups on its own.
   */
  operation: string;

  /** Searchable/filterable in Sentry. Keep values low cardinality. */
  tags?: { [key: string]: string };

  /** Anything that helps debug. Must not contain user entered content. */
  extra?: { [key: string]: any };
}

/**
 * Report an error we caught and handled ourselves to Sentry.
 *
 * The global `SentryErrorHandler` only sees errors that make it to Angular's
 * `ErrorHandler`, and it deliberately drops GraphQL and network errors. Errors
 * we swallow with `catchError` and surface as a toast never reach it, which
 * leaves us with a red toast and nothing to debug. Use this at those catch
 * sites to send the underlying error plus enough context to act on it.
 */
export function reportHandledError(error: any, context: HandledErrorContext) {
  const detail = describeError(error);

  // eslint-disable-next-line no-console
  console.error(context.operation, error, { ...context.extra, detail });

  if (!environment.enableSentry) {
    return;
  }

  Sentry.withScope(scope => {
    scope.setUser({ username: localStorage.getItem('uid') });
    scope.setTag('handled_error', context.operation);
    scope.setTag('device_platform', getDevicePlatform());

    Object.keys(context.tags || {}).forEach(key => scope.setTag(key, context.tags[key]));
    Object.keys(context.extra || {}).forEach(key => scope.setExtra(key, context.extra[key]));
    scope.setExtra('error_detail', detail);

    // Wrap in a real Error so Sentry has a title and stacktrace to group on.
    // The original error is preserved in the `error_detail` extra.
    Sentry.captureException(new Error(`${context.operation}: ${errorSummary(error)}`));
  });
}

/** One line, low cardinality description used for Sentry grouping. */
function errorSummary(error: any): string {
  if (error == null) {
    return 'unknown error';
  }

  if (isHttpError(error)) {
    return `HTTP ${error.status} ${error.statusText || ''}`.trim();
  }

  const graphQLErrors = error.graphQLErrors as any[];
  if (graphQLErrors?.length > 0) {
    return truncate(graphQLErrors.map(e => e?.message).join(' | '), MAX_SUMMARY_LENGTH);
  }

  if (error.networkError) {
    return `network error: ${errorSummary(error.networkError)}`;
  }

  return truncate(error.message || String(error), MAX_SUMMARY_LENGTH);
}

/** Everything we know about the error, flattened so Sentry can render it. */
function describeError(error: any) {
  if (error == null) {
    return null;
  }

  if (isHttpError(error)) {
    return {
      kind: 'HttpErrorResponse',
      status: error.status,
      statusText: error.statusText,
      url: error.url,
      message: error.message,
      // The response body is where a Rails exception, an nginx `413 Request
      // Entity Too Large` page, or a validation message actually shows up.
      body: describeBody(error.error)
    };
  }

  const graphQLErrors = error.graphQLErrors as any[];
  if (graphQLErrors?.length > 0 || error.networkError) {
    return {
      kind: 'ApolloError',
      message: error.message,
      graphQLErrors: (graphQLErrors || []).map(e => ({
        message: e?.message,
        path: e?.path,
        extensions: e?.extensions
      })),
      networkError: error.networkError ? describeError(error.networkError) : null
    };
  }

  return {
    kind: error.constructor?.name || typeof error,
    message: error.message || String(error),
    stack: error.stack
  };
}

function describeBody(body: any): string {
  if (body == null) {
    return null;
  }

  // A status 0 / CORS / dropped connection failure gives us an event, not a body.
  if (typeof Event !== 'undefined' && body instanceof Event) {
    return `[${body.type} event - request never completed]`;
  }

  try {
    return truncate(typeof body === 'string' ? body : JSON.stringify(body), MAX_BODY_LENGTH);
  } catch {
    return truncate(String(body), MAX_BODY_LENGTH);
  }
}

function truncate(value: string, max: number): string {
  const text = String(value ?? '');
  return text.length > max ? `${text.slice(0, max)}...[truncated]` : text;
}

function isHttpError(error: any): error is HttpErrorResponse {
  // Duck typed as well as `instanceof`, because Apollo wraps its own network
  // errors and they do not always survive as `HttpErrorResponse` instances.
  return error instanceof HttpErrorResponse
    || (typeof error?.status === 'number' && 'statusText' in error);
}
